import { createSelector } from 'reselect';
import type { Selector } from 'reselect';

import type {
  IMetadataState,
  IOfficeLocation,
  IPaymentTerm,
  ITransportMethod,
  PaymentTermApplyFor,
} from './types';

export const selectOfficeLocations = (state: IMetadataState) => state.officeLocations;
export const selectPaymentTerms = (state: IMetadataState) => state.paymentTerms;
export const selectTransportMethods = (state: IMetadataState) => state.transportMethods;

export const selectIndexedOfficeLocations = createSelector(
  selectOfficeLocations,
  (officeLocations) =>
    officeLocations.reduce((indexed: { [x: string]: any }, officeLocation: IOfficeLocation) => {
      if (officeLocation.locationCode) {
        indexed[officeLocation.locationCode] = officeLocation;
      }
      return indexed;
    }, {}),
);

export const selectOfficeLocation: Selector<IMetadataState> = createSelector(
  [selectIndexedOfficeLocations, (_: IMetadataState, locationCode: string) => locationCode],
  (indexedOfficeLocations, locationCode) => indexedOfficeLocations[locationCode],
);

export const selectIndexedPaymentTerms = createSelector(selectPaymentTerms, (paymentTerms) =>
  paymentTerms.reduce((indexed: { [x: string]: any }, paymentTerm: IPaymentTerm) => {
    if (paymentTerm.paymentTermsCode) {
      indexed[paymentTerm.paymentTermsCode] = paymentTerm;
    }
    return indexed;
  }, {}),
);

export const selectFilteredPaymentTerms: Selector<IMetadataState> = createSelector(
  [selectPaymentTerms, (_: IMetadataState, applyFor?: PaymentTermApplyFor) => applyFor],
  (paymentTerms, applyFor) => {
    if (!applyFor) return paymentTerms;

    return paymentTerms.filter((paymentTerm) => {
      if (!paymentTerm.applyFor || !paymentTerm.applyFor.length) return true;

      return paymentTerm.applyFor.includes(applyFor);
    });
  },
);

export const selectPaymentTerm: Selector<IMetadataState> = createSelector(
  [selectIndexedPaymentTerms, (_: IMetadataState, paymentTermsCode: string) => paymentTermsCode],
  (indexedPaymentTerms, paymentTermsCode) => indexedPaymentTerms[paymentTermsCode],
);

export const selectIndexedTransportMethods = createSelector(
  selectTransportMethods,
  (transportMethod) =>
    transportMethod.reduce((indexed: { [x: string]: any }, transportMethod: ITransportMethod) => {
      if (transportMethod.value) {
        indexed[transportMethod.value] = transportMethod;
      }
      return indexed;
    }, {}),
);

export const selectTransportMethod: Selector<IMetadataState> = createSelector(
  [selectIndexedTransportMethods, (_: IMetadataState, value: string) => value],
  (indexedTransportMethods, value) => indexedTransportMethods[value],
);
